/**
 * src/App.tsx
 * Root Application Component with Providers, Role-Aware Routing, and Live Approvals Badge
 */

import React, { useState, useEffect } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider, useToast } from './context/ToastContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { AppShell } from './components/layout/AppShell';
import { LoginView } from './views/LoginView';
import { DashboardView } from './views/DashboardView';
import { LedgerView } from './views/LedgerView';
import { UsersView } from './views/UsersView';
import { ApprovalsView } from './views/ApprovalsView';
import { CategoriesView } from './views/CategoriesView';
import { ProfileView } from './views/ProfileView';
import { AdminEntryModal } from './components/ledger/AdminEntryModal';
import { UserSubmissionModal } from './components/approvals/UserSubmissionModal';
import { AppSplashScreen } from './components/ui/AppSplashScreen';
import { PushPermissionBanner } from './components/notifications/PushPermissionBanner';
import { useApprovals } from './hooks/useApprovals';
import { useUsers } from './hooks/useUsers';
import { api, type CategoryItem } from './services/api';
import { hasActiveOverlays } from './utils/modalNavigation';
import { Layers } from 'lucide-react';
import './styles/global.css';

const MainApp: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { success, error: toastError } = useToast();
  const isAdmin = user?.role === 'admin';

  const [currentTab, setCurrentTab] = useState<string>('dashboard');
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const { users, refetch: refetchUsers } = useUsers();

  // Modals
  const [isAdminEntryOpen, setIsAdminEntryOpen] = useState(false);
  const [isUserSubmissionOpen, setIsUserSubmissionOpen] = useState(false);

  // Splash Screen State: Only run once per session (when site first opens, NOT on every reload)
  const [showSplash, setShowSplash] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const sessionSeen = sessionStorage.getItem('ckaudit_splash_seen');
    return !sessionSeen;
  });

  const lastBackPressRef = React.useRef<number>(0);

  // Synchronize history state for native app-like tab navigation & back button
  useEffect(() => {
    if (!isAuthenticated) return;

    // Set initial history state if empty
    if (!window.history.state?.tab) {
      window.history.replaceState({ tab: 'dashboard' }, '');
    }

    const handlePopState = (e: PopStateEvent) => {
      // If modal or overlay was open, it was handled in capture phase by modalNavigation
      if (hasActiveOverlays()) {
        return;
      }

      if (e.state?.tab) {
        setCurrentTab(e.state.tab);
      } else {
        // Popped to root or foreign state
        setCurrentTab((prevTab) => {
          if (prevTab !== 'dashboard') {
            // Take user back to dashboard!
            try {
              window.history.pushState({ tab: 'dashboard' }, '');
            } catch {}
            return 'dashboard';
          }

          // If already on dashboard: double-back to exit prevention
          const now = Date.now();
          if (now - lastBackPressRef.current < 2200) {
            // Allowed to exit app
            return 'dashboard';
          }

          lastBackPressRef.current = now;
          // Keep user inside dashboard and show native warning
          try {
            window.history.pushState({ tab: 'dashboard' }, '');
          } catch {}
          toastError('Press back again to exit CKAudit');
          return 'dashboard';
        });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAuthenticated, toastError]);

  const handleTabChange = (newTab: string) => {
    if (newTab === currentTab) return;
    try {
      window.history.pushState({ tab: newTab }, '');
    } catch {}
    setCurrentTab(newTab);
  };

  useEffect(() => {
    // Mark splash as shown for this browser session
    sessionStorage.setItem('ckaudit_splash_seen', 'true');

    const handlePreviewSplash = () => {
      setShowSplash(true);
    };
    window.addEventListener('ckaudit:preview_splash', handlePreviewSplash);
    return () => {
      window.removeEventListener('ckaudit:preview_splash', handlePreviewSplash);
    };
  }, []);

  // Live approvals state & count
  const {
    pendingCount,
    fetchPendingCount,
    submitRequest,
  } = useApprovals();

  // Load categories on mount & refresh helper
  const fetchCategories = async () => {
    try {
      const res = await api.categories.list();
      setCategories(res.categories || []);
    } catch (err) {
      console.error('Failed to load categories', err);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchCategories();
    }
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <>
        {showSplash && <AppSplashScreen onFinished={() => setShowSplash(false)} />}
        <div className="ambient-background flex-center" style={{ minHeight: '100vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, var(--indigo) 0%, var(--emerald) 100%)',
                margin: '0 auto 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'var(--indigo-glow)',
                color: '#ffffff',
                animation: 'pulseGlow 2s infinite ease-in-out'
              }}
            >
              <Layers size={28} />
            </div>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
              Loading CKAudit...
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        {showSplash && <AppSplashScreen onFinished={() => setShowSplash(false)} />}
        <LoginView />
      </>
    );
  }

  // Handle floating '+' action button click
  const handleNewEntryClick = () => {
    if (isAdmin) {
      setIsAdminEntryOpen(true);
    } else {
      setIsUserSubmissionOpen(true);
    }
  };

  // Direct transaction creation by Admin
  const handleAdminDirectEntry = async (data: any) => {
    try {
      const res = await api.transactions.create(data);
      success(`Transaction of ₹${data.amount} recorded to ledger!`, 'Transaction Saved');
      refetchUsers();
      return res;
    } catch (err: any) {
      toastError(err.message || 'Failed to save transaction');
      throw err;
    }
  };

  // User entry request submission
  const handleUserEntrySubmit = async (data: any) => {
    try {
      const res = await submitRequest(data);
      fetchPendingCount();
      return res;
    } catch (err: any) {
      throw err;
    }
  };

  // Category Master CRUD handlers
  const handleCategoryCreated = async (data: { name: string; icon: string; color: string }) => {
    await api.categories.create(data);
    await fetchCategories();
  };

  const handleCategoryUpdated = async (id: number, data: { name?: string; icon?: string; color?: string }) => {
    await api.categories.update(id, data);
    await fetchCategories();
  };

  const handleCategoryDeleted = async (id: number) => {
    await api.categories.delete(id);
    await fetchCategories();
  };

  return (
    <>
      {showSplash && <AppSplashScreen onFinished={() => setShowSplash(false)} />}
      <PushPermissionBanner />
      <AppShell
        currentTab={currentTab}
        onTabChange={handleTabChange}
        onNewEntryClick={handleNewEntryClick}
        pendingApprovalsCount={pendingCount}
      >
      {currentTab === 'dashboard' && (
        <DashboardView
          onNewEntryClick={handleNewEntryClick}
          onNavigateTab={handleTabChange}
          categories={categories}
          users={users}
          pendingApprovalsCount={pendingCount}
          onRefreshApprovals={fetchPendingCount}
        />
      )}

      {currentTab === 'ledger' && (
        <LedgerView />
      )}

      {currentTab === 'approvals' && (
        <ApprovalsView categories={categories} />
      )}

      {currentTab === 'users' && isAdmin && (
        <UsersView
          onViewUserStatement={(_userId) => {
            handleTabChange('ledger');
          }}
        />
      )}

      {currentTab === 'categories' && isAdmin && (
        <CategoriesView
          categories={categories}
          onRefresh={fetchCategories}
          onCategoryCreated={handleCategoryCreated}
          onCategoryUpdated={handleCategoryUpdated}
          onCategoryDeleted={handleCategoryDeleted}
        />
      )}

      {currentTab === 'profile' && (
        <ProfileView
          onNavigateTab={handleTabChange}
          onNewEntryClick={handleNewEntryClick}
        />
      )}

      {/* Admin Direct Entry Modal */}
      {isAdmin && (
        <AdminEntryModal
          isOpen={isAdminEntryOpen}
          onClose={() => setIsAdminEntryOpen(false)}
          onSubmit={handleAdminDirectEntry}
          users={users}
          categories={categories}
        />
      )}

      {/* User Submission Modal (Awaiting Approval) */}
      <UserSubmissionModal
        isOpen={isUserSubmissionOpen}
        onClose={() => setIsUserSubmissionOpen(false)}
        onSubmit={handleUserEntrySubmit}
        categories={categories}
      />
    </AppShell>
    </>
  );
};

export const App: React.FC = () => {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <NotificationProvider>
            <MainApp />
          </NotificationProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
};

export default App;
